import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import styled, { keyframes } from "styled-components";
import { breakpoints } from "../../../../../utils/breakpoints";
import { utilityLinks } from "../../../../../data/contact-info";
import HeroScene from "./HeroScene";
import ScrambleText from "./ScrambleText";

const ROTATION_MS = 9000;

function Banner() {
	const { t } = useTranslation();
	const router = useRouter();
	const [active, setActive] = useState(0);
	const mouseLayerRef = useRef(null);
	const scrollLayerRef = useRef(null);

	const slides = [
		{
			id: 1,
			titleLines: [
				[{ highlighted: false, text: t("banner.banner-1-title-1") }],
				[
					{ highlighted: false, text: t("banner.banner-1-title-2") },
					{ highlighted: true, text: t("banner.banner-1-title-3") },
				],
			],
			color: "#9FE870",
			highlight: "#B8F293",
			text: t("banner.banner-1-description"),
			buttonText: t("banner.banner-1-button"),
			href: `/${router.query.locale}/#contact-us`,
		},
		{
			id: 2,
			titleLines: [
				[{ highlighted: false, text: t("banner.banner-2-title-1") }],
				[{ highlighted: true, text: t("banner.banner-2-title-2") }],
			],
			color: "#CBFA05",
			highlight: "#DCFF4D",
			text: t("banner.banner-2-description"),
			buttonText: t("banner.banner-2-button"),
			href: utilityLinks.workWithUs,
		},
		{
			id: 3,
			titleLines: [
				[
					{ highlighted: false, text: t("banner.banner-3-title-1") },
					{ highlighted: true, text: t("banner.banner-3-title-2") },
				],
				[
					{ highlighted: true, text: t("banner.banner-3-title-3") },
					{ highlighted: true, text: t("banner.banner-3-title-4") },
				],
			],
			color: "#7D72FF",
			highlight: "#B7B0FF",
			text: t("banner.banner-3-description"),
			buttonText: t("banner.banner-3-button"),
			href: `/${router.query.locale}/#who-we-are`,
		},
	];

	const slide = slides[active];

	// auto-rotate the message
	useEffect(() => {
		const id = setTimeout(
			() => setActive((current) => (current + 1) % slides.length),
			ROTATION_MS,
		);
		return () => clearTimeout(id);
	}, [active, slides.length]);

	// mouse parallax on the content, applied directly to the ref (no re-renders).
	// intentionally no scroll-driven transform/fade: the content stays anchored
	// and leaves with the hero as one unit, matching the sculpture — a separate
	// scroll drift here made the text slide away out of sync with the animation
	useEffect(() => {
		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reducedMotion) return;

		let mx = 0;
		let my = 0;
		let rafId = null;

		const apply = () => {
			rafId = null;
			if (mouseLayerRef.current) {
				mouseLayerRef.current.style.transform = `translate3d(${mx * -14}px, ${my * -10}px, 0)`;
			}
		};

		const schedule = () => {
			if (rafId === null) rafId = requestAnimationFrame(apply);
		};

		const onPointerMove = (e) => {
			mx = (e.clientX / window.innerWidth) * 2 - 1;
			my = (e.clientY / window.innerHeight) * 2 - 1;
			schedule();
		};

		window.addEventListener("pointermove", onPointerMove, { passive: true });

		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			window.removeEventListener("pointermove", onPointerMove);
		};
	}, []);

	// embedded "<br/>" markers from the CMS copy are dropped on purpose:
	// they were tuned for the old layout and orphan single words per line
	const renderTitleLine = (line, lineIndex) => (
		<span className="headline-line" key={lineIndex}>
			{line.map((part, partIndex) => {
				const clean = part.text.replace("<br/>", "").trim();
				return (
					<React.Fragment key={partIndex}>
						{part.highlighted ? (
							<em className="highlighted">
								<ScrambleText text={clean} delay={lineIndex * 6} />
							</em>
						) : (
							<span>{clean}</span>
						)}{" "}
					</React.Fragment>
				);
			})}
		</span>
	);

	return (
		<Hero $color={slide.color} $highlight={slide.highlight}>
			<div className="backdrop" />
			<HeroScene accent={slide.color} shape={active} />
			{slides.map((s, i) => (
				<div
					key={s.id}
					className="glow"
					style={{
						background: `radial-gradient(circle at 71% 46%, ${s.color}2e 0%, transparent 55%)`,
						opacity: i === active ? 1 : 0,
					}}
				/>
			))}
			<div className="grid-overlay" />
			<div className="vignette" />
			{/* data URI lives in an inline style: stylis treats the "//" inside
			    the URL as a comment and corrupts every rule after it */}
			<div
				className="grain"
				style={{
					backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
				}}
			/>

			<div className="scroll-layer" ref={scrollLayerRef}>
				<div className="mouse-layer" ref={mouseLayerRef}>
					<div className="content" key={active}>
						<h1 className="headline">
							{slide.titleLines.map(renderTitleLine)}
						</h1>
						<p className="description">{slide.text}</p>
						<button
							className="cta"
							onClick={() => router.push(slide.href)}
						>
							{slide.buttonText}
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M3 8h10M9 4l4 4-4 4"
									stroke="currentColor"
									strokeWidth="1.6"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>

			<div className="bottom-bar">
				<div className="hero-nav">
					<span className="cue-label">{t("banner.scrollCue")}</span>
					<div className="cue-row">
						<button
							type="button"
							className="nav-arrow"
							aria-label={t("banner.prevSlide")}
							onClick={() =>
								setActive(
									(current) => (current + slides.length - 1) % slides.length,
								)
							}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 16 16"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M10 3 5 8l5 5"
									stroke="currentColor"
									strokeWidth="1.6"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<div className="scroll-cue" aria-hidden="true">
							<span className="wheel" />
						</div>
						<button
							type="button"
							className="nav-arrow"
							aria-label={t("banner.nextSlide")}
							onClick={() =>
								setActive((current) => (current + 1) % slides.length)
							}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 16 16"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="m6 3 5 5-5 5"
									stroke="currentColor"
									strokeWidth="1.6"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>
				</div>
				<div className="gptw">
					<img src="/landings/assets/icons/gptw.svg" alt="Great Place to Work" />
				</div>
			</div>
		</Hero>
	);
}

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(26px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const cueDrop = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  30% { opacity: 1; }
  100% { transform: translateY(14px); opacity: 0; }
`;

const floatBadge = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const Hero = styled.section`
  position: relative;
  height: 100vh;
  min-height: 600px;
  max-height: 1200px;
  overflow: hidden;
  background: #000104;

  @supports (height: 100svh) {
    height: 100svh;
  }

  .backdrop {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 20% 110%, #1f1e2f66 0%, transparent 55%),
      radial-gradient(ellipse at 85% -10%, #15191e 0%, transparent 60%),
      #000104;
  }

  .glow {
    position: absolute;
    inset: 0;
    transition: opacity 1.4s ease;
    pointer-events: none;
  }

  .grid-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
    background-size: 72px 72px;
    mask-image: radial-gradient(ellipse at 50% 45%, black 0%, transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse at 50% 45%, black 0%, transparent 75%);
  }

  .vignette {
    position: absolute;
    inset: 0;
    z-index: 6;
    pointer-events: none;
    background: radial-gradient(
      ellipse at 50% 45%,
      transparent 48%,
      rgba(0, 1, 4, 0.5) 100%
    );
  }

  .grain {
    position: absolute;
    inset: 0;
    z-index: 7;
    pointer-events: none;
    opacity: 0.05;
    background-size: 256px 256px;
  }

  .scroll-layer {
    position: relative;
    z-index: 8;
    height: 100%;
    display: flex;
    /* mobile: content anchored low so the 3D sculpture owns the upper half */
    align-items: flex-end;
    padding: 96px 0 104px 0;
    will-change: transform, opacity;
  }

  /* horizontal rhythm mirrors the topbar container (1536px, 24px inner
     gutters) so the content's left edge lines up with the verzel logo */
  .mouse-layer {
    will-change: transform;
    max-width: 1536px;
    width: 100%;
    margin: 0 auto;
    padding: 0 24px;
  }

  .content {
    max-width: 720px;
  }

  .headline {
    margin: 0 0 20px 0;
    color: #ffffff;
    font-family: "Hind Madurai";
    font-weight: 600;
    font-size: clamp(1.7rem, 5vw, 4.25rem);
    line-height: 1.16;
    letter-spacing: -0.5px;
    text-wrap: balance;

    /* globals.scss has "* { color: #210E3F }", which beats inheritance on
       every inner span — force the chain back to inherit */
    span,
    em {
      color: inherit;
    }

    .headline-line {
      display: block;
      animation: ${rise} 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;

      &:nth-child(2) {
        animation-delay: 0.12s;
      }
    }

    .highlighted {
      font-style: normal;
      font-weight: 700;
      color: ${(props) => props.$highlight};
      text-shadow: 0 0 34px ${(props) => props.$color}73;
      transition: color 0.8s ease, text-shadow 0.8s ease;
    }
  }

  .description {
    color: #d2d2d2;
    font-family: "Hind Madurai";
    font-size: clamp(1rem, 1.35vw, 1.25rem);
    line-height: 1.65;
    max-width: 600px;
    margin: 0 0 40px 0;
    animation: ${rise} 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.22s both;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-family: "Hind Madurai";
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1.6px;
    color: ${(props) => props.$highlight};
    background: transparent;
    border: 1.5px solid ${(props) => props.$color};
    border-radius: 100px;
    padding: 14px 34px;
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      box-shadow 0.3s ease,
      transform 0.2s ease;
    animation: ${rise} 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.32s both;

    /* globals' "* { color }" rule hits the svg and its path directly —
       restore currentColor so the arrow matches the label */
    svg,
    svg * {
      color: inherit;
    }

    svg {
      transition: transform 0.2s ease;
    }

    &:hover {
      background-color: ${(props) => props.$color};
      color: #000104;
      box-shadow: 0 0 42px ${(props) => props.$color}66;
      transform: translateY(-2px);

      svg {
        transform: translateX(4px);
      }
    }
  }

  .bottom-bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    /* above .scroll-layer (z-index 8), otherwise the full-height content
       layer swallows real mouse clicks aimed at the nav arrows */
    z-index: 9;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 0 24px 28px 24px;
    max-width: 1536px;
    margin: 0 auto;
    pointer-events: none;
  }

  .hero-nav {
    position: absolute;
    left: 50%;
    bottom: 22px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .cue-label {
      font-family: "Hind Madurai";
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 3.2px;
      color: rgba(255, 255, 255, 0.4);
      white-space: nowrap;
    }

    .cue-row {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .nav-arrow {
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;

      svg,
      svg * {
        color: inherit;
      }
      width: 32px;
      height: 32px;
      padding: 0;
      border-radius: 50%;
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.55);
      cursor: pointer;
      transition:
        border-color 0.2s ease,
        color 0.2s ease,
        transform 0.2s ease;

      &:hover {
        border-color: ${(props) => props.$color};
        color: ${(props) => props.$highlight};
        transform: translateY(-1px);
      }
    }
  }

  .scroll-cue {
    .wheel {
      position: relative;
      display: block;
      width: 24px;
      height: 38px;
      border: 1.5px solid rgba(255, 255, 255, 0.35);
      border-radius: 14px;

      &::after {
        content: "";
        position: absolute;
        top: 7px;
        left: 50%;
        width: 3px;
        height: 7px;
        margin-left: -1.5px;
        border-radius: 3px;
        background: ${(props) => props.$color};
        animation: ${cueDrop} 1.8s ease-in-out infinite;
      }
    }
  }

  .gptw {
    display: none;
    pointer-events: auto;

    img {
      width: 86px;
      height: auto;
      animation: ${floatBadge} 5s ease-in-out infinite;
    }
  }

  @media (min-width: ${breakpoints.m}) {
    .scroll-layer {
      align-items: center;
      padding: 120px 0 0 0;
    }

    .bottom-bar {
      padding: 0 24px 40px 24px;
      max-width: 1536px;
    }

    .hero-nav {
      bottom: 34px;
    }

    .gptw {
      display: block;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .headline .headline-line,
    .description,
    .cta {
      animation: none;
    }

    .scroll-cue .wheel::after,
    .gptw img {
      animation: none;
    }
  }
`;

export default Banner;
